import { useRef, useState } from 'react'
import ErrorBanner from './ErrorBanner.jsx'

/**
 * Read/write thumbnail for catalogue rows (Drug / LabTest / ImagingStudy).
 *
 * Renders a small 40×40 square preview of the image (or a neutral
 * placeholder when there's no image). When `editable` is true (i.e. the
 * caller is a platform admin), clicking the preview opens an inline modal
 * with a file picker that uploads the image via `onUpload(file)`. The
 * `onUpload` callback is given the chosen `File` and is expected to call
 * the appropriate `setDrugImage` / `setLabTestImage` / `setImagingStudyImage`
 * helper, then return the updated record (or void).
 *
 * Why not let pharmacy/lab/radiology staff edit? Because these images are
 * platform-curated metadata on the *global* catalogue (Drug, LabTest,
 * ImagingStudy), shared across every provider that stocks the item. The
 * matching ViewSets are gated by `ReadOnlyForAuthenticated | IsAdminRole`.
 */
export default function CatalogueImageThumb({
  url,
  alt = '',
  editable = false,
  onUpload,
  onClear,
  size = 40,
}) {
  const [open, setOpen] = useState(false)

  const sq = { width: size, height: size }

  const Preview = (
    <div
      className="rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center"
      style={sq}
    >
      {url ? (
        <img
          src={url}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="text-[9px] uppercase tracking-wide text-slate-400">no img</span>
      )}
    </div>
  )

  if (!editable) {
    // Read-only: just show the thumb. Hover-magnifier could come later.
    return Preview
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block hover:ring-2 hover:ring-wantok-300 rounded"
        title="Edit image (admin)"
      >
        {Preview}
      </button>
      {open && (
        <UploadModal
          alt={alt}
          currentUrl={url}
          onClose={() => setOpen(false)}
          onUpload={onUpload}
          onClear={onClear}
        />
      )}
    </>
  )
}

function UploadModal({ alt, currentUrl, onClose, onUpload, onClear }) {
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(currentUrl || null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function pickFile(f) {
    setError(null)
    if (!f) {
      setFile(null)
      setPreview(currentUrl || null)
      return
    }
    if (!/^image\//.test(f.type)) {
      setError(new Error('Please pick an image file (PNG / JPG / WEBP).'))
      return
    }
    if (f.size > 5 * 1024 * 1024) {
      setError(new Error('Image is over 5 MB — please pick a smaller file.'))
      return
    }
    setFile(f)
    const reader = new FileReader()
    reader.onload = (e) => setPreview(e.target?.result)
    reader.readAsDataURL(f)
  }

  async function save() {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      await onUpload(file)
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    if (!onClear) return
    if (!window.confirm('Remove this image?')) return
    setBusy(true)
    setError(null)
    try {
      await onClear()
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Catalogue image</div>
            <div className="text-xs text-slate-500 truncate">{alt}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
        </div>

        <div className="border border-slate-200 rounded bg-slate-50 p-3 flex items-center justify-center" style={{ minHeight: 160 }}>
          {preview ? (
            <img src={preview} alt={alt} className="max-h-40 object-contain" />
          ) : (
            <span className="text-xs text-slate-400">No image yet</span>
          )}
        </div>

        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => pickFile(e.target.files?.[0] || null)}
            className="text-xs"
          />
          <p className="text-[11px] text-slate-500 mt-1">PNG / JPG / WEBP up to 5 MB.</p>
        </div>

        {error && <ErrorBanner error={error} />}

        <div className="flex items-center justify-end gap-2 pt-1">
          {currentUrl && onClear && (
            <button
              onClick={clear}
              disabled={busy}
              className="text-xs text-rose-600 hover:underline disabled:opacity-40 mr-auto"
            >
              Remove image
            </button>
          )}
          <button
            onClick={onClose}
            disabled={busy}
            className="text-xs text-slate-600 hover:underline disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!file || busy}
            className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 disabled:opacity-40"
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  )
}
